// client/src/components/layout/AppLayout.jsx
import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Car, CalendarClock, AlertTriangle, FileText,
  History, Settings, Wrench, Users, Package, ShieldAlert,
  LogOut, Menu, Gauge, Receipt, TrendingUp, Eye, EyeOff, Mail, ClipboardList,
} from 'lucide-react'
import clsx from 'clsx'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../context/AuthContext'
import api from '../../api/client'

const customerNav = [
  { to: '/',          label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/vehicles',  label: 'Vehicles',  icon: Car },
  { to: '/schedule',  label: 'Schedule',  icon: CalendarClock },
  { to: '/repairs',   label: 'My Repairs', icon: Wrench },
  { to: '/history',   label: 'History',   icon: History },
  { to: '/emails',    label: 'Emails',    icon: Mail },
  { to: '/reports',   label: 'Reports',   icon: TrendingUp },
  { to: '/settings',  label: 'Settings',  icon: Settings },
]

const garageNav = [
  { to: '/garage',           label: 'Dashboard',        icon: Gauge, end: true },
  { to: '/garage/jobs',      label: 'Jobs',              icon: Wrench },
  { to: '/garage/customers', label: 'Customer Vehicles', icon: Car },
  { to: '/garage/parts',     label: 'Parts',             icon: Package },
  { to: '/garage/quotes',    label: 'Quotes',            icon: FileText },
  { to: '/garage/invoices',  label: 'Invoices',          icon: Receipt },
  { to: '/garage/checkins',  label: 'Check-Ins',         icon: ClipboardList },
  { to: '/garage/emails',    label: 'Emails',            icon: Mail },
  { to: '/reports',          label: 'Reports',           icon: TrendingUp },
  { to: '/settings',         label: 'Settings',          icon: Settings },
]

const adminExtra = [
  { to: '/admin', label: 'Admin Panel', icon: ShieldAlert },
]

function NavItem({ to, label, icon: Icon, end, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) => clsx(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
        isActive
          ? 'bg-indigo-600/15 text-indigo-400'
          : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)]'
      )}
    >
      <Icon size={16} />
      {label}
    </NavLink>
  )
}

function Sidebar({ onClose }) {
  const { user, logout, viewMode, realUser, switchToCustomerView, restoreView } = useAuth()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const isGarage = user?.role === 'garage_user' || user?.role === 'garage_admin'
  const isAdmin  = user?.role === 'platform_admin'

  // When in customer view the current role is 'customer', so check realUser for the original role.
  const realIsGarage = realUser?.role === 'garage_user' || realUser?.role === 'garage_admin'
  const realIsAdmin  = realUser?.role === 'platform_admin'

  const navItems = isAdmin ? garageNav : isGarage ? garageNav : customerNav

  const handleLogout = async () => {
    try { await api.post('/auth/logout') } catch { /* ignore */ }
    logout()
    navigate('/login')
  }

  const handleSwitchToCustomer = async () => {
    try {
      await switchToCustomerView()
      queryClient.clear()
      navigate('/')
      onClose?.()
    } catch (err) {
      console.error('Failed to switch to customer view:', err)
    }
  }

  const handleRestoreView = () => {
    restoreView()
    queryClient.clear()
    navigate('/')
    onClose?.()
  }

  const closeNav = () => onClose?.()

  // Show "Customer View" toggle when user is garage/admin in default mode.
  // Also show when in customer view (via realUser) so they can exit.
  const canSwitchToCustomer = viewMode === 'default' && (isGarage || isAdmin)
  const canExitView         = viewMode !== 'default'

  return (
    <div className="flex flex-col h-full bg-[var(--bg-surface)] border-r border-[var(--border)]">
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-[var(--border)] shrink-0">
        <div className="w-8 h-8 rounded-xl bg-indigo-600 flex items-center justify-center shadow-glow shrink-0">
          <Car size={15} className="text-white" />
        </div>
        <span className="font-display font-bold text-[var(--text-primary)]">Service Tracker</span>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {navItems.map(item => (
          <NavItem key={item.to} {...item} onClick={closeNav} />
        ))}

        {isAdmin && (
          <>
            <div className="pt-4 pb-1 px-3">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                Admin
              </p>
            </div>
            {adminExtra.map(item => (
              <NavItem key={item.to} {...item} onClick={closeNav} />
            ))}
          </>
        )}
      </nav>

      {/* View toggle controls */}
      {(canSwitchToCustomer || canExitView) && (
        <div className="px-3 pb-1">
          {canSwitchToCustomer && (
            <button
              onClick={handleSwitchToCustomer}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium
                         text-indigo-400 hover:bg-indigo-600/10 rounded-lg transition-colors"
            >
              <Eye size={13} />
              Customer View
            </button>
          )}
          {canExitView && (
            <button
              onClick={handleRestoreView}
              className="flex items-center gap-2 w-full px-3 py-2 text-xs font-medium
                         text-amber-400 hover:bg-amber-600/10 rounded-lg transition-colors"
            >
              <EyeOff size={13} />
              Exit {viewMode === 'impersonating' ? 'Impersonation' : 'Customer View'}
            </button>
          )}
        </div>
      )}

      {/* User footer */}
      <div className="p-3 border-t border-[var(--border)] shrink-0">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <div className="w-7 h-7 rounded-full bg-indigo-600/20 flex items-center justify-center
                          text-xs font-semibold text-indigo-400 shrink-0">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-[var(--text-primary)] truncate">{user?.name}</p>
            <p className="text-[10px] text-[var(--text-muted)] truncate capitalize">
              {user?.role?.replace(/_/g, ' ')}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            title="Sign out"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { viewMode, user, restoreView } = useAuth()
  const navigate    = useNavigate()
  const queryClient = useQueryClient()

  const handleRestoreFromBanner = () => {
    restoreView()
    queryClient.clear()
    navigate('/')
  }

  return (
    <div className="flex h-dvh overflow-hidden bg-[var(--bg-base)]">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex flex-col shrink-0 w-56">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-10 w-64 h-full">
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-[var(--border)]
                        bg-[var(--bg-surface)] lg:hidden shrink-0">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
          >
            <Menu size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Car size={12} className="text-white" />
            </div>
            <span className="font-display font-bold text-sm text-[var(--text-primary)]">
              Service Tracker
            </span>
          </div>
        </div>

        {/* Impersonation banner */}
        {viewMode === 'impersonating' && (
          <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-2
                          bg-amber-500/10 border-b border-amber-500/20">
            <p className="text-xs text-amber-400">
              Impersonating{' '}
              <span className="font-semibold">{user?.name}</span>
              {' '}({user?.email}) as{' '}
              <span className="capitalize">{user?.role?.replace(/_/g, ' ')}</span>
              {' '}— session expires in 1 hour.
            </p>
            <button
              onClick={handleRestoreFromBanner}
              className="shrink-0 text-xs font-medium text-amber-400 hover:text-amber-300
                         underline transition-colors whitespace-nowrap"
            >
              Stop impersonating
            </button>
          </div>
        )}

        {/* Customer view banner */}
        {viewMode === 'customer' && (
          <div className="shrink-0 flex items-center justify-between gap-4 px-4 py-2
                          bg-indigo-500/10 border-b border-indigo-500/20">
            <p className="text-xs text-indigo-400">
              Customer view — showing only your own vehicle and service data.
            </p>
            <button
              onClick={handleRestoreFromBanner}
              className="shrink-0 text-xs font-medium text-indigo-400 hover:text-indigo-300
                         underline transition-colors whitespace-nowrap"
            >
              Exit customer view
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
